import { create } from 'zustand';
import { doc, runTransaction, collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { TransactionType } from '../types/battery';

interface BatteryState {
  cells: number;
  maxCells: number;

  // Derived
  percentage: () => number;

  // Actions
  setCells: (cells: number) => void;
  setMaxCells: (max: number) => void;

  /**
   * Atomically consume cells. Returns true if successful (enough balance).
   */
  consume: (uid: string, amount: number, type: TransactionType) => Promise<boolean>;

  /**
   * Atomically charge cells.
   */
  charge: (uid: string, amount: number, type: TransactionType) => Promise<void>;
}

/** Write battery transaction history to users/{uid}/batteryHistory subcollection */
async function recordTransaction(uid: string, type: TransactionType, amount: number, balance: number) {
  try {
    await addDoc(collection(db, 'users', uid, 'batteryHistory'), {
      type,
      amount,
      timestamp: Date.now(),
      balance,
    });
  } catch (err) {
    console.error('[Battery] Failed to record transaction:', err);
  }
}

export const useBatteryStore = create<BatteryState>((set, get) => ({
  cells: 0,
  maxCells: 10_000_000,

  percentage: () => {
    const { cells, maxCells } = get();
    if (maxCells <= 0) return 0;
    return Math.min(100, Math.floor((cells / maxCells) * 100));
  },

  setCells: (cells) => set({ cells }),
  setMaxCells: (max) => set({ maxCells: max }),

  consume: async (uid, amount, type) => {
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const result = await runTransaction(db, async (tx) => {
          const userRef = doc(db, 'users', uid);
          const snap = await tx.get(userRef);
          const current = snap.data()?.battery ?? 0;
          if (current < amount) return { success: false, balance: current };
          const newBalance = current - amount;
          tx.update(userRef, { battery: newBalance });
          return { success: true, balance: newBalance };
        });

        if (result.success) {
          set((s) => ({ cells: Math.max(0, s.cells - amount) }));
          // Record transaction (fire-and-forget)
          recordTransaction(uid, type, -amount, result.balance);
        }
        return result.success;
      } catch (err) {
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, 100 * (attempt + 1)));
          continue;
        }
        console.error('[Battery] Consume failed after retries:', err);
        return false;
      }
    }
    return false;
  },

  charge: async (uid, amount, type) => {
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const newBalance = await runTransaction(db, async (tx) => {
          const userRef = doc(db, 'users', uid);
          const snap = await tx.get(userRef);
          const current = snap.data()?.battery ?? 0;
          const balance = current + amount;
          tx.update(userRef, { battery: balance });
          return balance;
        });
        set((s) => ({ cells: s.cells + amount }));
        // Record transaction (fire-and-forget)
        recordTransaction(uid, type, amount, newBalance);
        return;
      } catch (err) {
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, 100 * (attempt + 1)));
          continue;
        }
        console.error('[Battery] Charge failed after retries:', err);
      }
    }
  },
}));
